import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardHeader, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  BookText, Upload, Plus, Pencil, Trash2, ChevronRight, ChevronDown,
  FileSpreadsheet, Link2, AlertCircle, CheckCircle2, Wand2,
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TYPE_BADGES = {
  activo:          { label: 'Activo',          cls: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300' },
  pasivo:          { label: 'Pasivo',          cls: 'bg-rose-100 text-rose-700 dark:bg-rose-950/40 dark:text-rose-300' },
  patrimonio_neto: { label: 'P. Neto',         cls: 'bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300' },
  ingreso:         { label: 'Ingreso',         cls: 'bg-blue-100 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300' },
  egreso:          { label: 'Egreso',          cls: 'bg-violet-100 text-violet-700 dark:bg-violet-950/40 dark:text-violet-300' },
};

function userCan(user, mod, act) {
  if (!user) return false;
  if (user.role === 'admin') return true;
  return user.permissions?.[mod]?.[act] === true;
}

function buildTree(accounts) {
  const byCode = {};
  accounts.forEach(a => { byCode[a.code] = { ...a, children: [] }; });
  const roots = [];
  accounts.forEach(a => {
    if (a.parent_code && byCode[a.parent_code]) {
      byCode[a.parent_code].children.push(byCode[a.code]);
    } else {
      roots.push(byCode[a.code]);
    }
  });
  const sortRec = (n) => {
    n.children.sort((x, y) => x.code.localeCompare(y.code, undefined, { numeric: true }));
    n.children.forEach(sortRec);
  };
  roots.sort((x, y) => x.code.localeCompare(y.code, undefined, { numeric: true }));
  roots.forEach(sortRec);
  return roots;
}

function flattenTree(nodes, expanded, depth = 0, out = []) {
  for (const n of nodes) {
    out.push({ node: n, depth });
    if (expanded[n.code] && n.children && n.children.length > 0) {
      flattenTree(n.children, expanded, depth + 1, out);
    }
  }
  return out;
}

function AccountRow({ node, depth, hasChildren, isExpanded, toggle, onEdit, onDelete, canEdit, canDelete }) {
  const tb = TYPE_BADGES[node.type] || TYPE_BADGES.activo;
  return (
    <div
      className={`flex items-center gap-2 px-3 py-2 hover:bg-slate-50 dark:hover:bg-slate-900/50 border-b border-slate-100 dark:border-slate-800 transition-colors ${node.is_postable ? '' : 'bg-slate-50/60 dark:bg-slate-900/30 font-semibold'}`}
      style={{ paddingLeft: `${12 + depth * 18}px` }}
      data-testid={`account-row-${node.code}`}
    >
      <button
        type="button"
        className="w-5 h-5 flex items-center justify-center text-slate-500 hover:text-slate-900 dark:hover:text-slate-100"
        onClick={() => toggle(node.code)}
        disabled={!hasChildren}
        aria-label={isExpanded ? 'Colapsar' : 'Expandir'}
      >
        {hasChildren ? (isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />) : null}
      </button>
      <span className="font-mono text-[12px] text-slate-700 dark:text-slate-300 min-w-[120px]" data-testid={`account-code-${node.code}`}>{node.code}</span>
      <span className="flex-1 text-sm text-slate-900 dark:text-slate-100">{node.name}</span>
      <Badge variant="outline" className={`text-[10px] ${tb.cls}`}>{tb.label}</Badge>
      <Badge variant="outline" className="text-[10px] text-slate-600 dark:text-slate-400">
        {node.nature === 'deudora' ? 'Deudora' : 'Acreedora'}
      </Badge>
      {!node.is_postable && (
        <Badge variant="outline" className="text-[10px] text-slate-500">Grupo</Badge>
      )}
      <div className="flex items-center gap-1">
        {canEdit && (
          <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => onEdit(node)} data-testid={`account-edit-${node.code}`} title="Editar">
            <Pencil className="w-3.5 h-3.5" />
          </Button>
        )}
        {canDelete && (
          <Button size="icon" variant="ghost" className="h-7 w-7 text-rose-600 hover:text-rose-700" onClick={() => onDelete(node)} data-testid={`account-delete-${node.code}`} title="Borrar">
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        )}
      </div>
    </div>
  );
}

function ImportDialog({ open, onClose, onDone }) {
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [replaceAll, setReplaceAll] = useState(false);
  const [busy, setBusy] = useState(false);

  const handleSubmit = async () => {
    if (!file) {
      toast.error('Seleccioná un archivo Excel primero.');
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(
        `${BACKEND_URL}/api/accounting/accounts/import?replace_all=${replaceAll}`,
        fd,
        { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } },
      );
      const d = r.data;
      const detectedMsg = d.imputable_column_detected ? ' (columna IMPUTABLE detectada)' : '';
      toast.success(`Importadas ${d.leaf_accounts_created} imputables + ${d.grouper_accounts_created || 0} agrupadoras${detectedMsg}. Padres auto: ${d.parent_accounts_created}. Actualizadas: ${d.leaf_accounts_updated}.`);
      onDone();
      onClose();
    } catch (e) {
      toast.error('Error en la importación: ' + (e.response?.data?.detail || e.message));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="import-dialog">
        <DialogHeader>
          <DialogTitle>Importar Plan de Cuentas desde Excel</DialogTitle>
          <DialogDescription>
            El archivo debe tener columnas <b>Cuenta</b> y <b>Descripción</b>. Las cuentas padres se generan automáticamente.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label className="text-xs mb-1 block">Archivo .xlsx</Label>
            <Input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              data-testid="import-file-input"
            />
            {file && <p className="text-[11px] text-slate-500 mt-1">{file.name} · {(file.size / 1024).toFixed(1)} KB</p>}
          </div>
          <div className="flex items-center justify-between rounded-md border border-slate-200 dark:border-slate-800 px-3 py-2">
            <div>
              <p className="text-sm font-medium">Reemplazar todo el plan existente</p>
              <p className="text-[11px] text-slate-500">Borra cuentas y mapeos previos antes de importar.</p>
            </div>
            <Switch checked={replaceAll} onCheckedChange={setReplaceAll} data-testid="import-replace-all" />
          </div>
          {replaceAll && (
            <div className="flex items-start gap-2 text-[11px] text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/30 rounded-md p-2">
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>Esta operación es destructiva. Asegurate de no tener asientos contables ya cargados.</span>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy} data-testid="import-cancel">Cancelar</Button>
          <Button onClick={handleSubmit} disabled={busy || !file} data-testid="import-confirm">
            {busy ? 'Importando…' : 'Importar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AccountFormDialog({ open, account, onClose, onSaved }) {
  const isEdit = !!account?.account_id;
  const [code, setCode] = useState(account?.code || '');
  const [name, setName] = useState(account?.name || '');
  const [type, setType] = useState(account?.type || '');
  const [nature, setNature] = useState(account?.nature || '');
  const [isPostable, setIsPostable] = useState(account?.is_postable ?? true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setCode(account?.code || '');
    setName(account?.name || '');
    setType(account?.type || '');
    setNature(account?.nature || '');
    setIsPostable(account?.is_postable ?? true);
  }, [account]);

  const handleSubmit = async () => {
    if (!code || !name) {
      toast.error('Código y nombre son obligatorios.');
      return;
    }
    setBusy(true);
    try {
      if (isEdit) {
        await axios.put(`${BACKEND_URL}/api/accounting/accounts/${code}`, {
          name, type: type || undefined, nature: nature || undefined, is_postable: isPostable,
        }, { withCredentials: true });
        toast.success('Cuenta actualizada');
      } else {
        await axios.post(`${BACKEND_URL}/api/accounting/accounts`, {
          code, name, type: type || undefined, nature: nature || undefined, is_postable: isPostable,
        }, { withCredentials: true });
        toast.success('Cuenta creada');
      }
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al guardar');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="account-form-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Editar cuenta contable' : 'Nueva cuenta contable'}</DialogTitle>
          <DialogDescription>
            Si dejás Tipo o Naturaleza en blanco, se infieren del código (1.x=Activo, 2.x=Pasivo, 3.x=PN, 4.1.x=Ingreso, 4.2.x=Egreso).
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label className="text-xs mb-1 block">Código</Label>
            <Input
              value={code}
              onChange={(e) => setCode(e.target.value)}
              disabled={isEdit}
              placeholder="1.1.1.01.000"
              className="font-mono"
              data-testid="account-form-code"
            />
            <p className="text-[11px] text-slate-500 mt-1">Formato jerárquico con puntos. Los padres se crean solos.</p>
          </div>
          <div>
            <Label className="text-xs mb-1 block">Nombre</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="CAJA" data-testid="account-form-name" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs mb-1 block">Tipo</Label>
              <Select value={type || '__auto__'} onValueChange={(v) => setType(v === '__auto__' ? '' : v)}>
                <SelectTrigger data-testid="account-form-type"><SelectValue placeholder="Auto" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__auto__">Auto (según código)</SelectItem>
                  <SelectItem value="activo">Activo</SelectItem>
                  <SelectItem value="pasivo">Pasivo</SelectItem>
                  <SelectItem value="patrimonio_neto">Patrimonio Neto</SelectItem>
                  <SelectItem value="ingreso">Ingreso</SelectItem>
                  <SelectItem value="egreso">Egreso</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs mb-1 block">Naturaleza</Label>
              <Select value={nature || '__auto__'} onValueChange={(v) => setNature(v === '__auto__' ? '' : v)}>
                <SelectTrigger data-testid="account-form-nature"><SelectValue placeholder="Auto" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__auto__">Auto (según tipo)</SelectItem>
                  <SelectItem value="deudora">Deudora</SelectItem>
                  <SelectItem value="acreedora">Acreedora</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex items-center justify-between rounded-md border border-slate-200 dark:border-slate-800 px-3 py-2">
            <div>
              <p className="text-sm font-medium">Imputable</p>
              <p className="text-[11px] text-slate-500">Si está desactivado, sólo agrupa (no admite asientos directos).</p>
            </div>
            <Switch checked={isPostable} onCheckedChange={setIsPostable} data-testid="account-form-postable" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancelar</Button>
          <Button onClick={handleSubmit} disabled={busy} data-testid="account-form-save">
            {busy ? 'Guardando…' : 'Guardar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MappingsTab({ accounts, canEdit }) {
  const [unmapped, setUnmapped] = useState({ cash_bank: [], income_concept: [], expense_concept: [], withholding_type: [] });
  const [existing, setExisting] = useState([]);
  const [systemMaps, setSystemMaps] = useState([]);

  const postable = useMemo(() => accounts.filter(a => a.is_postable && a.is_active), [accounts]);

  const reload = useCallback(async () => {
    try {
      const [u, m, s] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/accounting/mappings/unmapped-entities`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/accounting/mappings`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/accounting/mappings/system`, { withCredentials: true }),
      ]);
      setUnmapped(u.data);
      setExisting(m.data || []);
      setSystemMaps(s.data || []);
    } catch (e) {
      toast.error('No se pudieron cargar los mapeos');
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const mappedByEntity = useMemo(() => {
    const m = {};
    existing.forEach(e => { m[`${e.entity_type}::${e.entity_id}`] = e; });
    return m;
  }, [existing]);

  const saveMapping = async (entityType, entityId, accountCode) => {
    try {
      await axios.post(`${BACKEND_URL}/api/accounting/mappings`, {
        entity_type: entityType, entity_id: entityId, account_code: accountCode,
      }, { withCredentials: true });
      toast.success('Mapeo guardado');
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al mapear');
    }
  };

  const removeMapping = async (mappingId) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/accounting/mappings/${mappingId}`, { withCredentials: true });
      toast.success('Mapeo borrado');
      reload();
    } catch (e) {
      toast.error('Error al borrar mapeo');
    }
  };

  const renderEntityGroup = (title, entityType, items) => {
    if (!items || items.length === 0) {
      return (
        <div className="text-sm text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-950/30 rounded-md p-3 flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4" /> {title}: todo mapeado.
        </div>
      );
    }
    return (
      <div>
        <h4 className="text-sm font-semibold mb-2 text-slate-700 dark:text-slate-300">{title}</h4>
        <div className="space-y-1.5">
          {items.map(it => (
            <div key={it.id} className="flex items-center gap-2 bg-amber-50 dark:bg-amber-950/30 rounded-md px-3 py-2" data-testid={`unmapped-${entityType}-${it.id}`}>
              <span className="flex-1 text-sm">
                {it.name}
                {it.legacy_code && <span className="text-[10px] text-slate-500 ml-2 font-mono">legacy: {it.legacy_code}</span>}
              </span>
              <Select onValueChange={(v) => saveMapping(entityType, it.id, v)} disabled={!canEdit}>
                <SelectTrigger className="w-[280px]"><SelectValue placeholder="Mapear a cuenta…" /></SelectTrigger>
                <SelectContent>
                  {postable.map(a => (
                    <SelectItem key={a.code} value={a.code} className="font-mono text-xs">
                      {a.code} · {a.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-2">
          <div>
            <h3 className="text-base font-semibold">🔧 Cuentas de Sistema</h3>
            <p className="text-xs text-slate-500">El motor de asientos automáticos necesita estas cuentas asignadas a una cuenta del plan.</p>
          </div>
          {canEdit && (
            <Button
              size="sm"
              variant="default"
              className="bg-violet-600 hover:bg-violet-700 text-white shrink-0"
              onClick={async () => {
                try {
                  const r = await axios.post(
                    `${BACKEND_URL}/api/accounting/mappings/auto-system?apply=true`,
                    {},
                    { withCredentials: true },
                  );
                  const applied = r.data.applied_count;
                  const noMatch = (r.data.results || []).filter(x => x.status === 'no_match');
                  if (applied > 0) {
                    toast.success(`${applied} cuenta(s) de sistema mapeadas automáticamente`);
                  } else {
                    toast.info('No se aplicaron nuevos mapeos.');
                  }
                  if (noMatch.length > 0) {
                    toast.warning(`Sin coincidencia automática: ${noMatch.map(n => n.label).join(', ')}. Asignalas manualmente abajo.`, { duration: 7000 });
                  }
                  reload();
                } catch (e) {
                  toast.error(e.response?.data?.detail || 'Error en auto-mapeo');
                }
              }}
              data-testid="auto-map-system-btn"
              title="Sugiere automáticamente las 7 cuentas de sistema buscándolas por nombre en el plan"
            >
              <Wand2 className="w-4 h-4 mr-1.5" /> Auto-mapear sistema
            </Button>
          )}
        </CardHeader>
        <CardContent>
          <div className="space-y-1.5">
            {systemMaps.map(sm => (
              <div key={sm.entity_type} className={`flex items-center gap-2 rounded-md px-3 py-2 ${sm.account_code ? 'bg-slate-50 dark:bg-slate-900/40' : 'bg-amber-50 dark:bg-amber-950/30'}`} data-testid={`system-map-${sm.entity_type}`}>
                <span className="flex-1 text-sm">{sm.label}</span>
                {sm.account_code && (
                  <span className="text-xs font-mono text-slate-700 dark:text-slate-300">{sm.account_code}</span>
                )}
                <Select
                  value={sm.account_code || undefined}
                  onValueChange={(v) => saveMapping(sm.entity_type, 'system', v)}
                  disabled={!canEdit}
                >
                  <SelectTrigger className="w-[280px]"><SelectValue placeholder={sm.account_code ? 'Cambiar…' : 'Asignar cuenta…'} /></SelectTrigger>
                  <SelectContent>
                    {postable.map(a => (
                      <SelectItem key={a.code} value={a.code} className="font-mono text-xs">
                        {a.code} · {a.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <h3 className="text-base font-semibold">📋 Entidades sin mapear</h3>
          <p className="text-xs text-slate-500">Asigná cada cash_bank, concept y retención a su cuenta contable.</p>
        </CardHeader>
        <CardContent className="space-y-4">
          {renderEntityGroup('Cajas y Bancos', 'cash_bank', unmapped.cash_bank)}
          {renderEntityGroup('Conceptos de Ingreso', 'income_concept', unmapped.income_concept)}
          {renderEntityGroup('Conceptos de Egreso', 'expense_concept', unmapped.expense_concept)}
          {renderEntityGroup('Retenciones', 'withholding_type', unmapped.withholding_type)}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <h3 className="text-base font-semibold">✅ Mapeos vigentes</h3>
        </CardHeader>
        <CardContent>
          {existing.length === 0 ? (
            <p className="text-sm text-slate-500 italic">Aún no hay mapeos.</p>
          ) : (
            <div className="space-y-1">
              {existing.map(m => (
                <div key={m.mapping_id} className="flex items-center gap-2 text-xs py-1.5 border-b border-slate-100 dark:border-slate-800 last:border-0">
                  <Badge variant="outline" className="text-[10px] shrink-0">{m.entity_type}</Badge>
                  <span className="text-slate-700 dark:text-slate-300 truncate max-w-[180px]" title={m.entity_name || m.entity_id}>
                    {m.entity_name || m.entity_id}
                  </span>
                  <span className="text-slate-400 shrink-0">→</span>
                  <span className="font-mono text-slate-500 shrink-0">{m.account_code}</span>
                  {m.account_name && <span className="text-slate-700 dark:text-slate-300 truncate" title={m.account_name}>· {m.account_name}</span>}
                  <div className="flex-1" />
                  {canEdit && (
                    <Button size="icon" variant="ghost" className="h-6 w-6 text-rose-600 shrink-0" onClick={() => removeMapping(m.mapping_id)} title="Borrar mapeo">
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default function ChartOfAccounts() {
  const { user } = useOutletContext();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [importOpen, setImportOpen] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [activeTab, setActiveTab] = useState('plan');

  const canView = userCan(user, 'contabilidad', 'ver');
  const canCreate = userCan(user, 'contabilidad', 'crear');
  const canEdit = userCan(user, 'contabilidad', 'editar');
  const canDelete = userCan(user, 'contabilidad', 'eliminar');

  const loadAccounts = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${BACKEND_URL}/api/accounting/accounts`, { withCredentials: true });
      setAccounts(r.data || []);
    } catch (e) {
      toast.error('No se pudo cargar el plan de cuentas');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (canView) loadAccounts(); }, [loadAccounts, canView]);

  const tree = useMemo(() => buildTree(accounts), [accounts]);

  const toggle = (code) => setExpanded(p => ({ ...p, [code]: !p[code] }));
  const expandAll = () => {
    const all = {};
    accounts.forEach(a => { all[a.code] = true; });
    setExpanded(all);
  };
  const collapseAll = () => setExpanded({});

  const handleDelete = async (acc) => {
    if (!window.confirm(`¿Borrar la cuenta ${acc.code} · ${acc.name}?`)) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/accounting/accounts/${acc.code}`, { withCredentials: true });
      toast.success('Cuenta borrada');
      loadAccounts();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo borrar');
    }
  };

  const counts = useMemo(() => {
    const t = { total: accounts.length, postable: 0, by_type: {} };
    accounts.forEach(a => {
      if (a.is_postable) t.postable++;
      t.by_type[a.type] = (t.by_type[a.type] || 0) + 1;
    });
    return t;
  }, [accounts]);

  if (!canView) {
    return (
      <div className="p-6">
        <Card><CardContent className="p-6 text-center text-slate-500">No tenés permisos para ver el plan de cuentas.</CardContent></Card>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="chart-of-accounts-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <BookText className="w-6 h-6 text-teal-600" />
            Plan de Cuentas
          </h1>
          <p className="text-sm text-slate-500">
            {counts.total} cuentas · {counts.postable} imputables ·
            {Object.entries(counts.by_type).map(([t, n]) => ` ${TYPE_BADGES[t]?.label || t}: ${n}`).join(' ·')}
          </p>
        </div>
        <div className="flex gap-2">
          {canCreate && (
            <Button variant="outline" size="sm" onClick={() => setImportOpen(true)} data-testid="open-import-btn">
              <Upload className="w-4 h-4 mr-1.5" /> Importar Excel
            </Button>
          )}
          {canCreate && (
            <Button size="sm" onClick={() => { setEditingAccount(null); setFormOpen(true); }} data-testid="new-account-btn">
              <Plus className="w-4 h-4 mr-1.5" /> Nueva cuenta
            </Button>
          )}
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="plan" data-testid="tab-plan">📒 Plan de Cuentas</TabsTrigger>
          <TabsTrigger value="mappings" data-testid="tab-mappings">
            <Link2 className="w-3.5 h-3.5 mr-1" /> Mapeos
          </TabsTrigger>
        </TabsList>

        <TabsContent value="plan" className="mt-3">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div className="flex gap-1.5">
                <Button size="sm" variant="ghost" onClick={expandAll} className="text-xs h-7">Expandir todo</Button>
                <Button size="sm" variant="ghost" onClick={collapseAll} className="text-xs h-7">Colapsar todo</Button>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {loading ? (
                <div className="p-8 text-center text-slate-500">Cargando…</div>
              ) : tree.length === 0 ? (
                <div className="p-8 text-center text-slate-500">
                  <FileSpreadsheet className="w-12 h-12 mx-auto mb-2 opacity-40" />
                  <p>Sin cuentas cargadas todavía.</p>
                  {canCreate && (
                    <Button size="sm" className="mt-3" onClick={() => setImportOpen(true)}>
                      <Upload className="w-4 h-4 mr-1.5" /> Importar plan desde Excel
                    </Button>
                  )}
                </div>
              ) : (
                <div data-testid="accounts-tree">
                  {flattenTree(tree, expanded).map(({ node, depth }) => (
                    <AccountRow
                      key={node.code}
                      node={node}
                      depth={depth}
                      hasChildren={!!(node.children && node.children.length)}
                      isExpanded={!!expanded[node.code]}
                      toggle={toggle}
                      onEdit={(a) => { setEditingAccount(a); setFormOpen(true); }}
                      onDelete={handleDelete}
                      canEdit={canEdit}
                      canDelete={canDelete}
                    />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="mappings" className="mt-3">
          <MappingsTab accounts={accounts} canEdit={canEdit} />
        </TabsContent>
      </Tabs>

      <ImportDialog open={importOpen} onClose={() => setImportOpen(false)} onDone={loadAccounts} />
      <AccountFormDialog
        open={formOpen}
        account={editingAccount}
        onClose={() => { setFormOpen(false); setEditingAccount(null); }}
        onSaved={loadAccounts}
      />
    </div>
  );
}
